import React, { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { useDesignColors } from '../../hooks/useDesignColors';
import { Subscription } from '../../hooks/useSubscription';
import { FeatureRestrictionModal } from '../subscription/FeatureRestrictionModal';

interface NewChatbotModalProps {
  onClose: () => void;
  onCreate: (name: string, description?: string) => void;
  isCreating: boolean;
  error: string | null;
  subscription: Subscription | null;
}

export function NewChatbotModal({ onClose, onCreate, isCreating, error, subscription }: NewChatbotModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [showRestrictionModal, setShowRestrictionModal] = useState(false);
  const { colors } = useDesignColors();
  
  // Check if user can create more chatbots based on subscription
  const canCreateChatbot = () => {
    if (!subscription) return false;
    
    // Enterprise tier has unlimited chatbots
    if (subscription.tier === 'enterprise') return true;
    
    // Check limits for other tiers
    const limits = {
      free: 1,
      starter: 3,
      professional: 10
    };
    
    // Get current chatbot count from subscription metadata if available
    const currentCount = 1; // Default to 1 (the current chatbot)
    
    return currentCount < limits[subscription.tier as keyof typeof limits];
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canCreateChatbot()) {
      setShowRestrictionModal(true);
      return;
    }
    
    if (name.trim()) {
      onCreate(name.trim(), description.trim() || undefined);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-gray-900/95 rounded-xl border border-white/10 w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h3 className="text-xl font-semibold text-white">Create a new chatbot</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 text-red-400 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
              Chatbot name <span className="text-red-400">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="My Awesome Chatbot"
              required
            />
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
              Description (optional)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="A brief description of your chatbot"
              rows={3}
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || !name.trim()}
              className="px-4 py-2 rounded-lg text-white transition-all flex items-center gap-2"
              style={{
                background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                opacity: isCreating || !name.trim() ? 0.7 : 1
              }}
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create chatbot'
              )}
            </button>
          </div>
        </form>
      </div>
      
      <FeatureRestrictionModal
        isOpen={showRestrictionModal}
        onClose={() => setShowRestrictionModal(false)}
        feature="Multiple chatbots"
      />
    </div>
  );
}